import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { Snapshot, SnapshotUploadStatus, Vdd, VddWithContent } from '@vi-iot/client';

import { AppState } from '../app.state';
import {
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsVddListParameter,
  vddToHide,
} from '../remote-diagnostics';
import { VddDetailsListSelectors, VddDetailsListState } from '../vdd-details-list';

import { BackupSeparator } from '../_common/backup-separator';
import { VddDetailsListHelper } from '../vdd-details-list/vdd-details-list.helper';
import { SnapshotType, VddCurrentAndSnapshotValue } from './remote-diagnostics-snapshots.model';
import {
  RemoteDiagnosticsSnapshotsState,
  SHOW_MORE_BACKUPS_THRESHOLD,
} from './remote-diagnostics-snapshots.state';

export namespace RemoteDiagnosticsSnapshotsSelectors {
  export const snapshotsState = (state: AppState): RemoteDiagnosticsSnapshotsState =>
    state.remoteDiagnosticsSnapshots;

  const VddDetailsList = (state: AppState): VddDetailsListState => state.vddDetailsList;

  export const showValues: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(snapshotsState, (state: RemoteDiagnosticsSnapshotsState) => state.showValues);

  export const isShowingMoreSnapshots = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState): boolean => state.isShowingMoreSnapshots
  );

  export function getSnapshots(): MemoizedSelector<
    AppState,
    Snapshot[],
    DefaultProjectorFn<Snapshot[]>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.snapshots
    );
  }

  export function getSnapshotsFiltered(): MemoizedSelector<
    AppState,
    Snapshot[],
    DefaultProjectorFn<Snapshot[]>
  > {
    return createSelector(snapshotsState, (state: RemoteDiagnosticsSnapshotsState) => {
      const matchingSnapshots = state.snapshots
        .filter((snapshot) => snapshot.status === SnapshotUploadStatus.UPLOADED)
        .filter((snapshot) => {
          const snapshotDate = new Date(snapshot.createdAt);

          return (
            snapshotDate.getMonth() === state.filterDate.month() &&
            snapshotDate.getFullYear() === state.filterDate.year()
          );
        });

      if (state.isShowingMoreSnapshots) {
        return matchingSnapshots;
      } else {
        return matchingSnapshots.slice(0, SHOW_MORE_BACKUPS_THRESHOLD);
      }
    });
  }

  export function getSnapshotsFilteredLength(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(snapshotsState, (state: RemoteDiagnosticsSnapshotsState) => {
      const matchingSnapshots = state.snapshots
        .filter((snapshot) => snapshot.status === SnapshotUploadStatus.UPLOADED)
        .filter((snapshot) => {
          const snapshotDate = new Date(snapshot.createdAt);

          return (
            snapshotDate.getMonth() === state.filterDate.month() &&
            snapshotDate.getFullYear() === state.filterDate.year()
          );
        });

      return matchingSnapshots.length;
    });
  }

  export function getCurrentSnapshotType(): MemoizedSelector<
    AppState,
    SnapshotType,
    DefaultProjectorFn<SnapshotType>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.snapshotType
    );
  }

  export function getShowSnapshotsLoader(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.fetchSnapshotsLoading
    );
  }

  export function getShowCreateSnapshotLoader(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.createSnapshotLoading
    );
  }

  export function getShowFetchSnapshotDetailsLoader(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.fetchSnapshotDetailsLoading
    );
  }

  export const getInstallationVddList: MemoizedSelector<
    AppState,
    Vdd[],
    DefaultProjectorFn<Vdd[]>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.installationVDDList
  );

  export const getInstallationVddListWithoutSeparator: MemoizedSelector<
    AppState,
    Vdd[],
    DefaultProjectorFn<Vdd[]>
  > = createSelector(snapshotsState, (state: RemoteDiagnosticsSnapshotsState) =>
    state.installationVDDList.map((vdd) => {
      return { ...vdd, key: BackupSeparator.removeBackupSeparator(vdd.key) };
    })
  );

  export const getInstallationVddListLength: MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.installationVDDList.length
  );

  export const getSelectedParametersForNewSnapshot: MemoizedSelector<
    AppState,
    Vdd[],
    DefaultProjectorFn<Vdd[]>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.selectedParametersForNewSnapshot
  );

  export const getSelectedParametersForNewSnapshotLength: MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.selectedParametersForNewSnapshot.length
  );

  export function getSelectedSnapshot(): MemoizedSelector<
    AppState,
    Snapshot,
    DefaultProjectorFn<Snapshot>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.selectedSnapshot
    );
  }

  export function getSnapshotDetails(): MemoizedSelector<
    AppState,
    VddWithContent[],
    DefaultProjectorFn<VddWithContent[]>
  > {
    return createSelector(
      snapshotsState,
      (state: RemoteDiagnosticsSnapshotsState) => state.snapshotDetails
    );
  }

  export const getVddsForNewSnapshotWithContent: MemoizedSelector<
    AppState,
    VddWithContent[],
    DefaultProjectorFn<VddWithContent[]>
  > = createSelector(
    VddDetailsList,
    snapshotsState,
    (vddDetailsListState, state: RemoteDiagnosticsSnapshotsState) => {
      if (state.selectedParametersForNewSnapshot?.length) {
        return vddDetailsListState.vddDetailsList
          .filter((vddWithDetails) =>
            state.selectedParametersForNewSnapshot.some((snapshotVdd) =>
              matchVddWithSelectedParameter(
                snapshotVdd,
                vddWithDetails.vddName,
                vddWithDetails.logicalLinkName
              )
            )
          )
          .filter((vdd) => vdd.singleRead && vdd.structure)
          .map((rawVddDetail) => {
            const vddWithContent: VddWithContent = {
              key: BackupSeparator.addBackupSeparator(
                rawVddDetail.vddName,
                rawVddDetail.logicalLinkName
              ),
              structure: rawVddDetail.structure,
              values: rawVddDetail.singleRead,
              logicalLink: rawVddDetail.logicalLinkName,
            };

            return vddWithContent;
          });
      } else {
        return [];
      }
    }
  );

  export const getVddsToCancel: MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > = createSelector(
    getInstallationVddList,
    getSelectedParametersForNewSnapshot,
    VddDetailsList,
    snapshotsState,
    (
      installationVddList,
      vddsForNewSnapshot,
      vddDetailsListState,
      state: RemoteDiagnosticsSnapshotsState
    ) => {
      const isSnapshotType = state.snapshotType === SnapshotType.SNAPSHOT;
      const vddsForSnapshot = isSnapshotType ? vddsForNewSnapshot : installationVddList;

      if (vddsForSnapshot?.length) {
        return vddDetailsListState.vddDetailsList
          .filter((vddWithDetails) =>
            vddsForSnapshot.some((snapshotVdd) =>
              matchVddWithSelectedParameter(
                snapshotVdd,
                vddWithDetails.vddName,
                vddWithDetails.logicalLinkName
              )
            )
          )
          .filter((vdd) => !vdd.singleRead || !vdd.structure)
          .map((rawVddDetail) =>
            BackupSeparator.addBackupSeparator(rawVddDetail.vddName, rawVddDetail.logicalLinkName)
          );
      } else {
        return [];
      }
    }
  );

  export const getVddsWithContent: MemoizedSelector<
    AppState,
    VddWithContent[],
    DefaultProjectorFn<VddWithContent[]>
  > = createSelector(
    getInstallationVddList,
    VddDetailsList,
    (vddListForSnapshot, vddDetailsListState) => {
      if (vddListForSnapshot?.length) {
        return vddDetailsListState.vddDetailsList
          .filter((vddWithDetails) =>
            vddListForSnapshot.some((snapshotVdd) =>
              matchVddWithSelectedParameter(
                snapshotVdd,
                vddWithDetails.vddName,
                vddWithDetails.logicalLinkName
              )
            )
          )
          .filter((vdd) => vdd.singleRead && vdd.structure)
          .map((rawVddDetail) => {
            const vddWithContent: VddWithContent = {
              key: BackupSeparator.addBackupSeparator(
                rawVddDetail.vddName,
                rawVddDetail.logicalLinkName
              ),
              structure: rawVddDetail.structure,
              values: rawVddDetail.singleRead,
              logicalLink: rawVddDetail.logicalLinkName,
            };

            return vddWithContent;
          });
      } else {
        return [];
      }
    }
  );

  export const getVddsForBackupWithContent: MemoizedSelector<
    AppState,
    VddWithContent[],
    DefaultProjectorFn<VddWithContent[]>
  > = createSelector(
    VddDetailsList,
    snapshotsState,
    (vddDetailsListState, state: RemoteDiagnosticsSnapshotsState) => {
      if (state.installationVDDList?.length) {
        return vddDetailsListState.vddDetailsList
          .filter((vddWithDetails) =>
            state.installationVDDList.some((snapshotVdd) =>
              matchVddWithSelectedParameter(
                snapshotVdd,
                vddWithDetails.vddName,
                vddWithDetails.logicalLinkName
              )
            )
          )
          .filter((vdd) => vdd.singleRead && vdd.structure)
          .map((rawVddDetail) => {
            const vddWithContent: VddWithContent = {
              key: BackupSeparator.addBackupSeparator(
                rawVddDetail.vddName,
                rawVddDetail.logicalLinkName
              ),
              structure: rawVddDetail.structure,
              values: rawVddDetail.singleRead,
              logicalLink: rawVddDetail.logicalLinkName,
            };

            return vddWithContent;
          });
      } else {
        return [];
      }
    }
  );

  export const isAnyFromSnapshotVddListLoading: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    getSelectedParametersForNewSnapshot,
    VddDetailsList,
    (vddListForSnapshot, vddDetailsListState) => {
      if (vddListForSnapshot?.length) {
        return (
          vddDetailsListState.vddDetailsList
            .filter((vddWithDetails) =>
              vddListForSnapshot.some((snapshotVdd) =>
                matchVddWithSelectedParameter(
                  snapshotVdd,
                  vddWithDetails.vddName,
                  vddWithDetails.logicalLinkName
                )
              )
            )
            .findIndex(
              (rawVddDetails) => rawVddDetails.singleReadLoading || rawVddDetails.structureLoading
            ) !== -1
        );
      } else {
        return false;
      }
    }
  );

  export const isAnyFromBackupVddListLoading: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    getInstallationVddList,
    VddDetailsList,
    (installationVddList, vddDetailsListState) => {
      if (installationVddList?.length) {
        return (
          vddDetailsListState.vddDetailsList
            .filter((vddWithDetails) =>
              installationVddList.some((snapshotVdd) =>
                matchVddWithSelectedParameter(
                  snapshotVdd,
                  vddWithDetails.vddName,
                  vddWithDetails.logicalLinkName
                )
              )
            )
            .findIndex(
              (rawVddDetails) => rawVddDetails.singleReadLoading || rawVddDetails.structureLoading
            ) !== -1
        );
      } else {
        return false;
      }
    }
  );

  export const howManySnapshotVddLoaded: MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > = createSelector(
    getInstallationVddList,
    VddDetailsList,
    (vddListForSnapshot, vddDetailsListState) => {
      if (vddListForSnapshot?.length) {
        const vddForSnapshot = vddDetailsListState.vddDetailsList.filter((vddWithDetails) =>
          vddListForSnapshot.some((snapshotVdd) =>
            matchVddWithSelectedParameter(
              snapshotVdd,
              vddWithDetails.vddName,
              vddWithDetails.logicalLinkName
            )
          )
        );

        return (
          vddForSnapshot.length -
          vddForSnapshot.filter(
            (rawVddDetails) => rawVddDetails.singleReadLoading || rawVddDetails.structureLoading
          ).length +
          1
        );
      } else {
        return 0;
      }
    }
  );

  export const getDateOfVddValuesRead: MemoizedSelector<
    AppState,
    Date,
    DefaultProjectorFn<Date>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.finishedReadingVddsAtDate
  );

  export function getVddSnapshotAndCurrentValues(
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<
    AppState,
    VddCurrentAndSnapshotValue,
    DefaultProjectorFn<VddCurrentAndSnapshotValue>
  > {
    return createSelector(
      VddDetailsListSelectors.selectMatchedValues(vddName, logicalLink),
      snapshotsState,
      (currentReadMatchedValues, state) => {
        const vddSnapshot = state.snapshotDetails?.find(
          (snapshot) => snapshot.key === vddName && snapshot.logicalLink === logicalLink
        );
        const vddCompareModel: VddCurrentAndSnapshotValue = {
          currentValue: currentReadMatchedValues,
          snapshotValue: VddDetailsListHelper.getMatchValues(
            VddDetailsListHelper.getFlattenedStructure(vddSnapshot?.structure),
            VddDetailsListHelper.getFlattenedSingleRead(vddSnapshot?.values)
          ),
        };

        return vddCompareModel;
      }
    );
  }

  export const getLastsNumberOfSnapshots: MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > = createSelector(snapshotsState, (state): number => state.lastsNumberOfSnapshots);

  export const isSnapshotCreated = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState): boolean => state.createSnapshotSuccess
  );

  export const getSelectedControlUnit: MemoizedSelector<
    AppState,
    RemoteDiagnosticsControlUnit,
    DefaultProjectorFn<RemoteDiagnosticsControlUnit>
  > = createSelector(
    snapshotsState,
    (state: RemoteDiagnosticsSnapshotsState) => state.selectedControlUnit
  );

  export const getAvailableParametersExceptSelected: MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddListParameter[],
    DefaultProjectorFn<RemoteDiagnosticsVddListParameter[]>
  > = createSelector(snapshotsState, (state: RemoteDiagnosticsSnapshotsState) =>
    state.vddListForSelectedControlUnit.filter(
      (vdd) =>
        !state.selectedParametersForNewSnapshot.some((parameter) =>
          matchVddWithSelectedParameter(parameter, vdd.name, state.selectedControlUnit.value)
        ) && vdd.name !== vddToHide
    )
  );

  export function isSelectedSnapshotParameterInvalid(
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(VddDetailsList, (vddDetailsListState): boolean => {
      const vddWithLogicalLink = BackupSeparator.addBackupSeparator(vddName, logicalLink);
      const vddDetailsListMatchingElement = vddDetailsListState.vddDetailsList.find(
        (vdd) => vdd.vddName === vddWithLogicalLink
      );

      return !!(
        vddDetailsListMatchingElement?.singleReadError ||
        vddDetailsListMatchingElement?.metadataError ||
        vddDetailsListMatchingElement?.structureError
      );
    });
  }
}

export function matchVddWithSelectedParameter(
  selectedParameter: Vdd,
  vddNameToMatch: string,
  selectedLogicalLink: string
): boolean {
  return (
    selectedParameter.key.includes(vddNameToMatch) &&
    selectedParameter.logicalLink === selectedLogicalLink
  );
}
